// Pilot usage events (#324): the smallest honest instrument. Five events
// answer the pilot's two questions (week-2 return, play-minutes per user);
// everything here is built so that analytics can never cost the music
// anything — recording is synchronous queueing, flushing is batched and
// best-effort, and every failure path is silence plus retry, never a thrown
// error or a user-visible message.
//
// Events are recorded ONLY while the auth state is 'active' (an activated
// pilot member): signed-out users and signed-in-but-uncoded accounts leave no
// rows, matching the issue's privacy contract and the RLS policy server-side
// (which would refuse them anyway — the client gate just avoids doomed
// requests). Rows carry the invite batch so cohorts separate in SQL.

import { getAuthState, getSupabaseRaw, subscribeAuth } from '@/auth/session';

/** The allow-listed feature names `feature_used` may carry. */
const FEATURES = new Set([
  'edition',
  'lens',
  'page-view',
  'export',
  'focus',
  'setlist-share',
  'setlist-open',
  'love',
  'card-add-to-playlist',
]);

const FLUSH_INTERVAL_MS = 15_000;
const QUEUE_CAP = 200;
/** A play shorter than this is a stray tap, not practice. */
const MIN_PLAY_MS = 5_000;

interface QueuedEvent {
  event: string;
  payload: Record<string, unknown>;
  at: string;
  /** Who it was recorded under: it is only ever sent as that user. */
  userId: string;
}

/** The structural slice of supabase-js the writer needs (tests fake it). */
export interface EventsClient {
  auth: {
    getSession(): Promise<{ data: { session: { user: { id: string } } | null } }>;
  };
  from(table: string): {
    insert(rows: Record<string, unknown>[]): Promise<{ error: { message: string } | null }>;
  };
}

/** The slice of ChordPlayer the play-minutes accumulator needs. */
export interface UsagePlayer {
  onTransportChange(listener: (state: 'idle' | 'playing' | 'paused') => void): () => void;
}

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;
let initialized = false;
let signedInRecorded = false;
let playStartedAt: number | null = null;

let testClient: EventsClient | null = null;
let testNow: (() => number) | null = null;

const now = () => (testNow ? testNow() : Date.now());

function client(): EventsClient | null {
  return testClient ?? (getSupabaseRaw() as EventsClient | null);
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushUsageEvents();
  }, FLUSH_INTERVAL_MS);
}

/** Queue an event; a no-op unless the user is an activated pilot member. */
export function recordEvent(event: string, payload: Record<string, unknown> = {}): void {
  const auth = getAuthState();
  if (auth.status !== 'active' || !auth.userId) return;
  queue.push({
    event,
    payload: auth.batch ? { ...payload, batch: auth.batch } : payload,
    at: new Date(now()).toISOString(),
    userId: auth.userId,
  });
  if (queue.length > QUEUE_CAP) queue = queue.slice(queue.length - QUEUE_CAP);
  scheduleFlush();
}

/** `feature_used`, names allow-listed so payloads stay a vocabulary. */
export function recordFeature(name: string): void {
  if (!FEATURES.has(name)) return;
  recordEvent('feature_used', { feature: name });
}

/**
 * Send everything queued, in one insert. Failures keep the batch for the
 * next attempt (the cap bounds growth); success clears exactly what was
 * sent, so events recorded during the request are never lost.
 *
 * The user id comes from the session at FLUSH time, so a row only goes out
 * when that session is the user it was recorded under. One recorded under
 * someone else (a sign-out this tab never heard about, then the next
 * student's sign-in) can never be sent honestly, and is dropped.
 */
export async function flushUsageEvents(): Promise<void> {
  if (flushing || queue.length === 0) return;
  const c = client();
  if (!c) return;
  flushing = true;
  // A snapshot, not the live array: recordEvent keeps pushing while the
  // request is out, and the cap can swap the queue for a new array.
  const batch = queue.slice();
  try {
    const { data } = await c.auth.getSession();
    const userId = data.session?.user.id;
    // No session while still active here (a token refresh that failed
    // offline): keep the batch and retry. A sign-out empties the queue.
    if (!userId) return;
    const own = batch.filter((e) => e.userId === userId);
    let done = batch.filter((e) => e.userId !== userId);
    if (own.length > 0) {
      const rows = own.map((e) => ({
        user_id: userId,
        event: e.event,
        payload: e.payload,
        at: e.at,
      }));
      const { error } = await c.from('pilot_events').insert(rows);
      if (!error) done = batch;
    }
    // Clear by identity: an offset into the queue counts events recorded
    // since, or points at the wrong ones once the cap has re-sliced it.
    const finished = new Set(done);
    queue = queue.filter((e) => !finished.has(e));
  } catch {
    // Network failure: keep the batch, the next schedule retries.
  } finally {
    flushing = false;
    if (queue.length > 0) scheduleFlush();
  }
}

function recordPlayChunk(endedAt: number) {
  if (playStartedAt === null) return;
  const elapsed = endedAt - playStartedAt;
  playStartedAt = null;
  if (elapsed < MIN_PLAY_MS) return;
  recordEvent('play_minutes', { minutes: Math.round((elapsed / 60_000) * 100) / 100 });
}

/**
 * Wire the instrument up: signed_in on activation (once per page load), the
 * play-minutes accumulator on the player's transport, and a best-effort
 * flush when the page hides. Idempotent; called once from App.
 */
export function initUsageAnalytics(player: UsagePlayer): void {
  if (initialized) return;
  initialized = true;

  subscribeAuth(() => {
    if (getAuthState().status !== 'active') {
      // Only an active user queues events, so whatever is still queued when
      // the state leaves 'active' belongs to the user who just left. On a
      // shared classroom device the next sign-in must not inherit it.
      queue = [];
      playStartedAt = null;
      return;
    }
    if (!signedInRecorded) {
      signedInRecorded = true;
      recordEvent('signed_in');
    }
  });
  // The subscription only fires on changes; an already-active session at
  // init time (boot completed first) records here.
  if (getAuthState().status === 'active') {
    signedInRecorded = true;
    recordEvent('signed_in');
  }

  player.onTransportChange((state) => {
    if (state === 'playing') {
      if (playStartedAt === null) {
        playStartedAt = now();
        recordEvent('play_started');
      }
    } else {
      recordPlayChunk(now());
    }
  });

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'hidden') return;
      // Background playback keeps accruing: bank the chunk so far and
      // restart the clock, then push what we have.
      if (playStartedAt !== null) {
        recordPlayChunk(now());
        playStartedAt = now();
      }
      void flushUsageEvents();
    });
  }
}

// ---------------------------------------------------------------------------
// Test seams

export function __setUsageTestHooks(hooks: {
  client?: EventsClient | null;
  now?: (() => number) | null;
}): void {
  if ('client' in hooks) testClient = hooks.client ?? null;
  if ('now' in hooks) testNow = hooks.now ?? null;
}

export function __resetUsageForTests(): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  queue = [];
  flushing = false;
  initialized = false;
  signedInRecorded = false;
  playStartedAt = null;
  testClient = null;
  testNow = null;
}

export function __queuedForTests(): QueuedEvent[] {
  return [...queue];
}
